import React from 'react';
import classnames from 'classnames';

// Components
import WpIcon from '../icon/WpIcon';
import { WpButton, WpButtonGroup } from '../button';
import { WpCollapsible } from '../collapsible';

import { useTranslation } from '../../../shared/utils/hooks/use-translation';

// Styles
import './WpStep.scss';

export interface Props {
  title: string;
  subTitle?: string;
  children?: React.ReactNode;
  active?: boolean;
  completed?: boolean;
  stepNumber?: number;
  handleNextNavigation?: () => void;
  handleBackNavigation?: () => void;
  onNextClick?: Function;
  hideDefaultNavButtons?: boolean;
  withAnimation?: boolean;
  isOpen?: boolean;
  titleBadge?: React.ReactNode;
}

const WpStep: React.FC<Props> = ({
  title,
  subTitle,
  children,
  active = false,
  completed = false,
  stepNumber = 1,
  handleNextNavigation,
  handleBackNavigation,
  onNextClick,
  hideDefaultNavButtons,
  withAnimation = true,
  isOpen = false,
  titleBadge,
}: Props) => {
  const { tr } = useTranslation();
  const classNamePrefix = 'WpStep';
  const classes = classnames(
    classNamePrefix,
    isOpen ? `${classNamePrefix}--open` : false,
    active && !completed ? `${classNamePrefix}--active` : false,
    completed ? `${classNamePrefix}--completed` : false,
    hideDefaultNavButtons ? `${classNamePrefix}--no-footer` : false
  );

  const handleNextClick = () => {
    if (onNextClick) {
      onNextClick();
    }
    handleNextNavigation();
  };

  return (
    <div className={classes}>
      <div className={`${classNamePrefix}__number`}>
        <div className={`${classNamePrefix}__number__digit`}>{completed ? <WpIcon name="check" /> : stepNumber}</div>
        <div className={`${classNamePrefix}__number__line`} />
      </div>

      <div className={`${classNamePrefix}__content`}>
        <p className={`${classNamePrefix}__title`}>
          {title}
          <span className={`${classNamePrefix}__title__badge`}> {titleBadge}</span>
        </p>

        <WpCollapsible open={active || isOpen} withAnimation={withAnimation}>
          <div className={`${classNamePrefix}__content__body`}>
            {subTitle && <span className={`${classNamePrefix}__subtitle`}>{subTitle}</span>}
            <div className={`${classNamePrefix}__content__body__children`}>{children}</div>
          </div>
          {!hideDefaultNavButtons && (
            <div className={`${classNamePrefix}__content__footer`}>
              <WpButtonGroup>
                {handleBackNavigation && (
                  <WpButton color="gray" variant="tertiary" onClick={handleBackNavigation}>
                    {tr('perform.common.back')}
                  </WpButton>
                )}
                {handleNextNavigation && <WpButton onClick={handleNextClick}>{tr('perform.common.next')}</WpButton>}
              </WpButtonGroup>
            </div>
          )}
        </WpCollapsible>
      </div>
    </div>
  );
};

export default WpStep;
